import { mapState, mapGetters } from 'vuex';
import ApiService from '@/services/apiService';
import get from 'lodash/get';
import compact from 'lodash/compact';
import cloneDeep from 'lodash/cloneDeep';
import pullAt from 'lodash/pullAt';
import debounce from 'lodash/debounce';
export default {
  name: 'CoreAlarmLogsReportWizardConfiguration',
  props: {
    reportModel: {
      type: Object,
      required: true
    },
    formValid: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      editModel: null,
      commands: [],
      executionResultPrompt: false,
      lastExecutionResult: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    reportExecuteConditions() {
      if (this.editModel.filters.originType === 'smartgroup-packet-monitoring') {
        return [{
          value: 'newAlarms',
          text: this.$i18n_t('AlarmLogs.reports.wizard.conditions.newThresholdAlarms')
        }, {
          value: 'new',
          text: this.$i18n_t('AlarmLogs.reports.wizard.conditions.thresholdAlarms')
        }];
      }
      return [{
        value: 'newErrors',
        text: this.$i18n_t('AlarmLogs.reports.wizard.conditions.newErrors')
      }, {
        value: 'new',
        text: this.$i18n_t('AlarmLogs.reports.wizard.conditions.new')
      }, {
        value: 'activeErrors',
        text: this.$i18n_t('AlarmLogs.reports.wizard.conditions.activeErrors')
      }, {
        value: 'active',
        text: this.$i18n_t('AlarmLogs.reports.wizard.conditions.active')
      }];
    },
    localeOptions() {
      return [{
        value: 'de-DE',
        text: this.$i18n_t('AlarmLogs.reports.wizard.locales.de-DE')
      }, {
        value: 'en-US',
        text: this.$i18n_t('AlarmLogs.reports.wizard.locales.en-US')
      }, {
        value: 'en-GB',
        text: this.$i18n_t('AlarmLogs.reports.wizard.locales.en-GB')
      }];
    },
    getColumnWidth() {
      return (param, paramConfig) => {
        if (paramConfig.width && ['m', 'l'].indexOf(paramConfig.width) >= 0) {
          return param.width === 'm' ? 6 : 12;
        }
        return 4;
      };
    },
    weekDayOptions() {
      return [{
        value: 1,
        text: this.$i18n_t('Common.Monday')
      }, {
        value: 2,
        text: this.$i18n_t('Common.Tuesday')
      }, {
        value: 3,
        text: this.$i18n_t('Common.Wednesday')
      }, {
        value: 4,
        text: this.$i18n_t('Common.Thursday')
      }, {
        value: 5,
        text: this.$i18n_t('Common.Friday')
      }, {
        value: 6,
        text: this.$i18n_t('Common.Saturday')
      }, {
        value: 0,
        text: this.$i18n_t('Common.Sunday')
      }];
    },
    monthDaysOptions() {
      const monthDays = Array.from({
        length: 31
      }, (idx, i) => i + 1);
      return monthDays;
    },
    actionsN20CardClass() {
      // in light-mode, the cards should have a slight darker-grey background,
      // in dark-mode they already have the appropriate separation
      return Boolean(this.$store.state.app.UiTheme) ? '' : 'dt-card-text-01';
    },
    tablesItems() {
      return [{
        value: 'all',
        text: this.$i18n_t('Common.all')
      }, {
        value: 'overview',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.overview')
      }, {
        value: 'newErrors',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.newErrors')
      }, {
        value: 'newWarnings',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.newWarnings')
      }, {
        value: 'activeErrors',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.activeErrors')
      }, {
        value: 'activeWarnings',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.activeWarnings')
      }, {
        value: 'resolvedErrors',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.resolvedErrors')
      }, {
        value: 'resolvedWarnings',
        text: this.$i18n_t('AlarmLogs.reports.wizard.tableItems.resolvedWarnings')
      }];
    }
  },
  watch: {
    reportModel: {
      handler: function () {
        this.editModel = cloneDeep(this.reportModel);
        this.handleTablesSelected(0);
      },
      deep: true,
      immediate: true
    },
    'editModel.tenantId': {
      handler: function () {
        this.fetchAccountRelatedData();
      },
      immediate: true
    },
    editModel: {
      handler: function () {
        // only publish changes if an actual value did change, not just a new cloned instance got assigned
        if (JSON.stringify(this.editModel) !== JSON.stringify(this.reportModel)) {
          this.publishChanges();
        }
      },
      deep: true
    }
  },
  methods: {
    fetchAccountRelatedData() {
      if (!get(this.editModel, 'tenantId')) {
        return;
      }
      this.fetchBridges().then(() => {
        // clear incorrect parameters properties from actions (params should match command manifest definition)
        this.editModel.actions.forEach(action => {
          const definedParamsKeys = ['locale', ...Object.keys(this.parametersForCommandId(action.command))];
          if (definedParamsKeys.length === 0) {
            return;
          }
          for (const key in action.params) {
            if (!definedParamsKeys.includes(key)) {
              delete action.params[key];
            }
          }
        });
      }).catch(error => {
        console.error('Error fetching bridges', error);
        this.$snack(this.$i18n_t('Connectors.Connectors.errors.errorWhileFetchingConnectos'), 'error');
      });
    },
    fetchBridges() {
      return Promise.all([ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          // only display bridges that have instance commands
          // AND are of type mail (only valid action for reports, for now)
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0 && type.type === 'bridge-mail';
        });
      }), ApiService.getBridgesForTenant(this.editModel.tenantId)]).then(results => {
        const bridgeTypeIdx = 0;
        const bridgesIdx = 1;
        let commands = [];
        results[bridgesIdx].forEach(bridgeInstance => {
          const bridgeTypeDef = results[bridgeTypeIdx].find(bridgeDef => bridgeDef.type === bridgeInstance.type);
          const instanceCmds = get(bridgeTypeDef, 'manifest.commands.instance', {});
          commands = commands.concat(Object.keys(instanceCmds).map(commandName => {
            if (instanceCmds[commandName].internal) {
              return null;
            }
            return {
              ...instanceCmds[commandName],
              name: `${bridgeInstance.name}.${commandName}`,
              id: `${bridgeInstance.id}.${commandName}`,
              bridgeType: bridgeInstance.type,
              config: get(bridgeInstance, 'bridge.config', {}),
              configId: bridgeInstance.id,
              manifest: bridgeTypeDef.manifest
            };
          }));
        });
        this.commands = compact(commands).filter(b => {
          const type = results[bridgeTypeIdx].find(t => t.type === b.bridgeType);
          return get(type, 'manifest.functionScope.invokeDirection.outbound', false);
        });
      });
    },
    addReportAction(index) {
      this.editModel.actions.splice(index, 0, {
        params: {
          subject: 'Alarmreport {{alarmReportName}} for {{smartGroupName}}',
          locale: 'en-US'
        },
        tables: ['all']
      });
      this.handleTablesSelected(index);
      this.editModel.actions = cloneDeep(this.editModel.actions);
    },
    deleteReportAction(index) {
      pullAt(this.editModel.actions, [index]);
      this.editModel.actions = cloneDeep(this.editModel.actions);
    },
    testExecuteAction(index) {
      ApiService.testAlarmReportAction(this, this.editModel, index).then(result => {
        this.lastExecutionResult = result;
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.executionSuccessful'), 'success', {
          message: this.$i18n_t('Common.viewDetails'),
          click: () => {
            this.executionResultPrompt = true;
          }
        });
      }).catch(error => {
        const errorRespData = get(error, 'response.data.data');
        const errorTranslation = errorRespData ? this.$i18n_t(`Error.${errorRespData}`) : null;
        let errorDetails = errorRespData && errorTranslation !== `Error.${errorRespData}` ? errorTranslation : get(error, 'response.data.data');
        if (typeof errorDetails === 'object') {
          errorDetails = JSON.stringify(errorDetails);
        }
        this.lastExecutionResult = get(error, 'response.data.message') ? `${error.response.data.message}: ${errorDetails}` : error.toString();
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.executionFailed'), 'error', {
          message: this.$i18n_t('Common.viewDetails'),
          click: () => {
            this.executionResultPrompt = true;
          }
        });
      });
    },
    commandAtIdxDidChange(index) {
      const changedAction = this.editModel.actions[index];
      const newParamsKeys = Object.keys(this.parametersForCommandId(changedAction.command));
      changedAction.params = changedAction.params || {};
      const currentParamsKeys = Object.keys(changedAction.params);
      const keysToRemove = currentParamsKeys.filter(key => !newParamsKeys.includes(key));
      keysToRemove.forEach(key => {
        delete this.editModel.actions[index].params[key];
      });
      const selectedCommand = this.editModel.actions[index];
      selectedCommand.params = selectedCommand.params || {};
    },
    findCommandById(commandId) {
      return this.commands.find(c => c.id === commandId);
    },
    orderedParamKeysForCommandId(commandId) {
      const parameters = this.parametersForCommandId(commandId);
      // hardcoded filter here, assume (for now) all commands are "sendMail" commands
      // the body (html/text) will not be configurable, and display the subject separately
      return Object.keys(parameters).filter(p => ['subject', 'text', 'html'].indexOf(p) < 0).sort((a, b) => {
        const orderIdxA = parameters[a].orderIdx || 0;
        const orderIdxB = parameters[b].orderIdx || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    parametersForCommandId(commandId) {
      return get(this.findCommandById(commandId), 'parameters', {});
    },
    startDatePicked(dateObject) {
      this.editModel.timeTrigger.dateStart = new Date(dateObject.from * 1000).toISOString();
    },
    endDatePicked(dateObject) {
      this.editModel.timeTrigger.dateEnd = new Date(dateObject.from * 1000).toISOString();
    },
    handleDaysSelection(property) {
      const days = this.editModel.timeTrigger[property] || [];
      this.editModel.timeTrigger[property] = days.filter(day => !isNaN(parseInt(day)));
    },
    handleTablesSelected(index) {
      if (this.editModel.actions[index].tables.find(i => i === 'all')) {
        this.editModel.actions[index].tables = this.tablesItems.map(i => i.value).filter(i => i !== 'all');
      }
    },
    publishChanges: debounce(function () {
      this.$emit('change', this.editModel);
    }, 250)
  }
};